import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  CreditCard, Check, Loader2, Plus, Trash2, Star, Heart, 
  Calendar, AlertCircle, CheckCircle, Crown, Sparkles, Zap, Gift
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';

// Tier color configurations
const TIER_STYLES = {
  social: {
    gradient: 'from-teal-400 to-teal-600',
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    badge: 'bg-teal-500',
    icon: '🤝',
    ring: 'ring-teal-400'
  },
  free: {
    gradient: 'from-teal-400 to-teal-600',
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    badge: 'bg-teal-500',
    icon: '🤝',
    ring: 'ring-teal-400'
  },
  basic: {
    gradient: 'from-emerald-400 to-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    badge: 'bg-emerald-500',
    icon: '🌱',
    ring: 'ring-emerald-400'
  },
  premium: {
    gradient: 'from-purple-400 to-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    badge: 'bg-purple-500',
    icon: '⭐',
    ring: 'ring-purple-400'
  },
  gold: {
    gradient: 'from-amber-400 to-yellow-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    badge: 'bg-amber-500',
    icon: '👑',
    ring: 'ring-amber-400'
  }
};

const Subscription = () => {
  const { API, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [savedCards, setSavedCards] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [userTier, setUserTier] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);
  
  // Card setup
  const [showAddCard, setShowAddCard] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  
  // Donation
  const [donationAmount, setDonationAmount] = useState('');
  const [donationMessage, setDonationMessage] = useState('');
  const [selectedCardForDonation, setSelectedCardForDonation] = useState('');
  const [donationHistory, setDonationHistory] = useState([]);

  useEffect(() => {
    fetchPlans();
    fetchSavedCards();
    fetchCurrentSubscription();
    fetchUserTier();
    fetchDonationHistory();

    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/plans`);
      // API returns { plans: [...] } so we need to extract the array
      const plansData = response.data?.plans || response.data || [];
      setPlans(Array.isArray(plansData) ? plansData : []);
    } catch (error) {
      console.error('Error fetching plans:', error);
      setPlans([]);
    }
  };

  const fetchUserTier = async () => {
    // User tier is resolved via currentSubscription endpoint
    // No separate endpoint needed
  };

  const fetchSavedCards = async () => {
    // Cards feature requires valid Stripe config
    // Skip API call, use empty state
    setSavedCards([]);
  };

  const fetchCurrentSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/current`);
      setCurrentSubscription(response.data);
      // Also set userTier from the same response if available
      if (response.data?.tier || response.data?.subscription?.plan_id) {
        setUserTier({
          tier: response.data.tier || response.data.subscription?.plan_id || 'social',
          name: response.data.name || 'Plan Social',
          meal_plans_used_this_month: response.data.meal_plans_used_this_month || 0,
          features_enabled: response.data.features_enabled || { meal_plans_per_month: 1 }
        });
      }
    } catch (error) {
      // Silently handle - use default tier
      setUserTier({
        tier: 'social',
        name: 'Plan Social',
        meal_plans_used_this_month: 0,
        features_enabled: { meal_plans_per_month: 1 }
      });
    }
  };

  const fetchDonationHistory = async () => {
    try {
      const response = await axios.get(`${API}/donations/history`);
      setDonationHistory(response.data?.donated || response.data || []);
    } catch (error) {
      // Silently handle
      setDonationHistory([]);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/payments/verify?session_id=${sessionId}`);
      if (response.data.status === 'paid') {
        toast.success('Plată procesată cu succes! Mulțumim!');
        fetchCurrentSubscription();
        fetchUserTier();
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const handleSubscribe = async (planId) => {
    if (planId === 'social') return; // Plan social este gratuit, nu necesită plată
    
    setProcessingPlan(planId);
    setLoading(true);

    try {
      if (savedCards.length > 0) {
        const defaultCard = savedCards.find(c => c.is_default) || savedCards[0];
        const response = await axios.post(`${API}/subscriptions/create`, {
          plan_id: planId,
          payment_method_id: defaultCard.id,
        });
        
        if (response.data.status === 'active') {
          toast.success('Abonament activat cu succes!');
          fetchCurrentSubscription();
          fetchUserTier();
        } else if (response.data.client_secret) {
          toast.info('Se procesează plata...');
        }
      } else {
        const originUrl = window.location.origin;
        const response = await axios.post(`${API}/payments/checkout`, {
          plan_id: planId,
          origin_url: originUrl,
        });
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la procesarea abonamentului');
    } finally {
      setLoading(false);
      setProcessingPlan(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm('Ești sigur că vrei să anulezi abonamentul?')) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/subscriptions/cancel`);
      toast.success('Abonamentul va fi anulat la sfârșitul perioadei curente.');
      fetchCurrentSubscription();
      fetchUserTier();
    } catch (error) {
      toast.error('Eroare la anularea abonamentului');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCard = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/payments/cards/setup`);
      toast.info('În producție, aici ar apărea formularul Stripe securizat pentru card.');
      setTimeout(() => {
        toast.success('Card adăugat cu succes! (Demo)');
        setShowAddCard(false);
        fetchSavedCards();
      }, 1500);
    } catch (error) {
      toast.error('Eroare la adăugarea cardului');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (!confirm('Ești sigur că vrei să ștergi acest card?')) return;
    try {
      await axios.delete(`${API}/payments/cards/${cardId}`);
      toast.success('Card șters cu succes');
      fetchSavedCards();
    } catch (error) {
      toast.error('Eroare la ștergerea cardului');
    }
  };

  const handleDonate = async () => {
    if (!donationAmount || parseFloat(donationAmount) <= 0) {
      toast.error('Introdu o sumă validă');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/donations/create`, {
        amount: parseFloat(donationAmount),
        message: donationMessage,
        payment_method_id: selectedCardForDonation || null,
      });
      toast.success(response.data.message);
      setDonationAmount('');
      setDonationMessage('');
      fetchDonationHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la procesarea donației');
    } finally {
      setLoading(false);
    }
  };

  const currentTierStyle = TIER_STYLES[userTier?.tier || 'free'];

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      <Navbar />

      <div className="container mx-auto px-4 md:px-12 py-8" data-testid="subscription-page">
        <div className="animate-fade-in">
          
          {/* Current Tier Banner */}
          {userTier && (
            <div className={`mb-8 p-6 rounded-2xl bg-gradient-to-r ${currentTierStyle.gradient} text-white`}>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <span className="text-4xl">{currentTierStyle.icon}</span>
                  <div>
                    <p className="text-white/80 text-sm">Planul tău actual</p>
                    <h2 className="text-2xl font-bold">{userTier.name}</h2>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white/80 text-sm">Planuri generate luna aceasta</p>
                  <p className="text-xl font-bold">
                    {userTier.meal_plans_used_this_month} / {userTier.features_enabled.meal_plans_per_month === -1 ? '∞' : userTier.features_enabled.meal_plans_per_month}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="text-center mb-8">
            <h1 className="font-serif text-4xl md:text-5xl font-light text-[#1A4D2E] mb-2">
              Alege Planul Tău
            </h1>
            <p className="text-[#4a5568] text-lg">
              De la simplu la extraordinar - găsește ce ți se potrivește
            </p>
          </div>

          <Tabs defaultValue="plans" className="max-w-7xl mx-auto">
            <TabsList className="grid w-full grid-cols-4 mb-8">
              <TabsTrigger value="plans">Planuri</TabsTrigger>
              <TabsTrigger value="subscription">Abonament</TabsTrigger>
              <TabsTrigger value="cards">Carduri</TabsTrigger>
              <TabsTrigger value="donate">Donații</TabsTrigger>
            </TabsList>

            {/* Plans Tab */}
            <TabsContent value="plans">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {plans.map((plan, index) => {
                  const style = TIER_STYLES[plan.id] || TIER_STYLES.free;
                  const isProcessing = processingPlan === plan.id;
                  const isCurrentPlan = userTier?.tier === plan.id;
                  const isGold = plan.id === 'gold';
                  const isPremium = plan.id === 'premium';

                  return (
                    <div
                      key={plan.id}
                      className="relative animate-fade-in"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      {/* Popular/Exclusive Badge */}
                      {plan.popular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                          <Badge className="bg-purple-500 text-white px-4 py-1 text-sm shadow-lg">
                            <Star className="w-3 h-3 mr-1" /> Cel Mai Popular
                          </Badge>
                        </div>
                      )}
                      {plan.exclusive && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                          <Badge className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-4 py-1 text-sm shadow-lg">
                            <Crown className="w-3 h-3 mr-1" /> Exclusiv
                          </Badge>
                        </div>
                      )}

                      <Card className={`border-2 ${style.border} ${style.bg} hover:shadow-2xl transition-all duration-500 h-full overflow-hidden ${
                        isCurrentPlan ? `ring-4 ${style.ring}` : ''
                      } ${isGold ? 'relative overflow-visible' : ''}`}>
                        
                        {/* Gold Sparkle Effect */}
                        {isGold && (
                          <div className="absolute inset-0 pointer-events-none">
                            <div className="absolute top-4 right-4 text-amber-400 animate-pulse"><Sparkles className="w-6 h-6" /></div>
                            <div className="absolute bottom-20 left-4 text-amber-400 animate-pulse delay-300"><Sparkles className="w-4 h-4" /></div>
                          </div>
                        )}

                        {/* Header with Gradient */}
                        <div className={`h-2 bg-gradient-to-r ${style.gradient}`} />
                        
                        <CardHeader className="pt-8 pb-4">
                          <div className="flex items-center justify-between mb-4">
                            <span className="text-3xl">{style.icon}</span>
                            {isCurrentPlan && (
                              <Badge className={`${style.badge} text-white`}>
                                <CheckCircle className="w-3 h-3 mr-1" /> Activ
                              </Badge>
                            )}
                          </div>
                          
                          <CardTitle className="font-serif text-2xl text-[#1A4D2E]">
                            {plan.name}
                          </CardTitle>
                          
                          <div className="mt-4">
                            {plan.price === 0 ? (
                              <span className="text-3xl font-bold text-[#1A4D2E]">Gratuit</span>
                            ) : (
                              <>
                                <span className="text-4xl font-bold text-[#1A4D2E]">{plan.price}</span>
                                <span className="text-[#4a5568] ml-1">{plan.currency}/lună</span>
                              </>
                            )}
                          </div>
                        </CardHeader>

                        <CardContent className="pb-6">
                          <ul className="space-y-3 mb-6">
                            {plan.features.map((feature, i) => (
                              <li key={i} className="flex items-start text-[#4a5568]">
                                <div className={`w-5 h-5 ${style.badge} rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0`}>
                                  <Check className="w-3 h-3 text-white" />
                                </div>
                                <span className="text-sm">{feature}</span>
                              </li>
                            ))}
                          </ul>

                          {plan.price === 0 ? (
                            <Button
                              disabled={isCurrentPlan}
                              className="w-full rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300"
                            >
                              {isCurrentPlan ? 'Plan Actual' : 'Plan Gratuit'}
                            </Button>
                          ) : (
                            <Button
                              onClick={() => handleSubscribe(plan.id)}
                              disabled={loading || isCurrentPlan}
                              className={`w-full rounded-full transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 ${
                                isCurrentPlan 
                                  ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                                  : isGold
                                    ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white hover:from-amber-600 hover:to-yellow-600'
                                    : isPremium
                                      ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:from-purple-600 hover:to-purple-700'
                                      : `bg-gradient-to-r ${style.gradient} text-white`
                              }`}
                            >
                              {isProcessing ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : isCurrentPlan ? (
                                'Plan Actual'
                              ) : isGold ? (
                                <>
                                  <Crown className="w-4 h-4 mr-2" />
                                  Devino Gold
                                </>
                              ) : isPremium ? (
                                <>
                                  <Zap className="w-4 h-4 mr-2" />
                                  Upgrade la Premium
                                </>
                              ) : (
                                'Abonează-te'
                              )}
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>

              {/* Feature Comparison */}
              <Card className="mt-12 border-none card-shadow">
                <CardHeader>
                  <CardTitle className="font-serif text-2xl text-[#1A4D2E] text-center">
                    Comparație Detaliată
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-4 px-4">Funcționalitate</th>
                          <th className="text-center py-4 px-4">
                            <span className="text-teal-600">🤝 Social</span>
                          </th>
                          <th className="text-center py-4 px-4">
                            <span className="text-emerald-600">🌱 Basic</span>
                          </th>
                          <th className="text-center py-4 px-4">
                            <span className="text-purple-600">⭐ Premium</span>
                          </th>
                          <th className="text-center py-4 px-4">
                            <span className="text-amber-600">👑 Gold</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { feature: 'Planuri de mese / lună', social: '1', basic: '4', premium: '∞', gold: '∞' },
                          { feature: 'Acces comunitate socială', social: '✅', basic: '✅', premium: '✅', gold: '✅' },
                          { feature: 'Recomandări AI', social: '❌', basic: '✅', premium: '✅', gold: '✅' },
                          { feature: 'Liste de cumpărături', social: '❌', basic: '✅', premium: '✅', gold: '✅' },
                          { feature: 'Analiză sănătate', social: '❌', basic: '❌', premium: '✅', gold: '✅' },
                          { feature: 'Acces nutriționiști', social: '❌', basic: '❌', premium: '✅', gold: '✅' },
                          { feature: 'Rețete chefi profesioniști', social: '❌', basic: '❌', premium: '✅', gold: '✅' },
                          { feature: 'Consultații 1-la-1', social: '❌', basic: '❌', premium: '❌', gold: '✅' },
                          { feature: 'Asistent 24/7', social: '❌', basic: '❌', premium: '❌', gold: '✅' },
                          { feature: 'Rețete VIP Michelin', social: '❌', basic: '❌', premium: '❌', gold: '✅' },
                          { feature: 'Evenimente exclusive', social: '❌', basic: '❌', premium: '❌', gold: '✅' },
                        ].map((row, i) => (
                          <tr key={i} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4 text-[#4a5568]">{row.feature}</td>
                            <td className="text-center py-3 px-4">{row.social}</td>
                            <td className="text-center py-3 px-4">{row.basic}</td>
                            <td className="text-center py-3 px-4">{row.premium}</td>
                            <td className="text-center py-3 px-4 font-medium text-amber-600">{row.gold}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Current Subscription Tab */}
            <TabsContent value="subscription">
              <Card className="border-none card-shadow">
                <CardHeader>
                  <CardTitle className="font-serif text-2xl text-[#1A4D2E]">
                    Abonamentul Tău
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {currentSubscription?.has_subscription ? (
                    <div className="space-y-6">
                      <div className={`p-6 rounded-2xl ${currentTierStyle.bg} ${currentTierStyle.border} border-2`}>
                        <div className="flex items-center gap-4 mb-4">
                          <span className="text-4xl">{currentTierStyle.icon}</span>
                          <div>
                            <h3 className="text-xl font-bold text-[#1A4D2E]">
                              {currentSubscription.subscription.plan_id.charAt(0).toUpperCase() + 
                               currentSubscription.subscription.plan_id.slice(1)}
                            </h3>
                            <Badge className={`${currentTierStyle.badge} text-white`}>
                              {currentSubscription.subscription.status === 'active' ? 'Activ' : 'Inactiv'}
                            </Badge>
                          </div>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-[#4a5568]">Început perioadă:</span>
                            <p className="font-medium">
                              {new Date(currentSubscription.subscription.current_period_start).toLocaleDateString('ro-RO')}
                            </p>
                          </div>
                          <div>
                            <span className="text-[#4a5568]">Sfârșit perioadă:</span>
                            <p className="font-medium">
                              {new Date(currentSubscription.subscription.current_period_end).toLocaleDateString('ro-RO')}
                            </p>
                          </div>
                        </div>
                      </div>

                      <Button
                        onClick={handleCancelSubscription}
                        disabled={loading}
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        Anulează Abonamentul
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Gift className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
                      <h3 className="font-serif text-xl text-[#1A4D2E] mb-2">
                        Nu ai un abonament activ
                      </h3>
                      <p className="text-[#4a5568] mb-6">
                        Alege un plan pentru a debloca toate funcționalitățile
                      </p>
                      <Button 
                        onClick={() => document.querySelector('[value="plans"]')?.click()}
                        className="bg-[#1A4D2E] text-white rounded-full"
                      >
                        Vezi Planurile
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Cards Tab */}
            <TabsContent value="cards">
              <Card className="border-none card-shadow">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-serif text-2xl text-[#1A4D2E]">
                      Carduri Salvate
                    </CardTitle>
                    <Button
                      onClick={() => setShowAddCard(true)}
                      className="bg-[#1A4D2E] text-white rounded-full"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Adaugă Card
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {savedCards.length > 0 ? (
                    <div className="space-y-4">
                      {savedCards.map((card) => (
                        <div
                          key={card.id}
                          className="flex items-center justify-between p-4 bg-[#F5F5F0] rounded-xl"
                        >
                          <div className="flex items-center gap-4">
                            <CreditCard className="w-8 h-8 text-[#1A4D2E]" />
                            <div>
                              <p className="font-medium">•••• •••• •••• {card.last4}</p>
                              <p className="text-sm text-[#4a5568]">
                                {card.brand} - Expiră {card.exp_month}/{card.exp_year}
                              </p>
                            </div>
                            {card.is_default && (
                              <Badge className="bg-[#9BCF53] text-white">Implicit</Badge>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            onClick={() => handleDeleteCard(card.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <CreditCard className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
                      <p className="text-[#4a5568]">Nu ai carduri salvate</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Donations Tab */}
            <TabsContent value="donate">
              <div className="grid md:grid-cols-2 gap-8">
                <Card className="border-none card-shadow">
                  <CardHeader>
                    <CardTitle className="font-serif text-2xl text-[#1A4D2E] flex items-center gap-2">
                      <Heart className="w-6 h-6 text-red-500" />
                      Fă o Donație
                    </CardTitle>
                    <CardDescription>
                      Susține comunitatea și ajută persoanele vulnerabile
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-4 gap-2">
                      {[10, 25, 50, 100].map((amount) => (
                        <Button
                          key={amount}
                          variant={donationAmount === String(amount) ? 'default' : 'outline'}
                          onClick={() => setDonationAmount(String(amount))}
                          className={donationAmount === String(amount) ? 'bg-[#1A4D2E]' : ''}
                        >
                          {amount} RON
                        </Button>
                      ))}
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Sumă personalizată</Label>
                      <Input
                        type="number"
                        value={donationAmount}
                        onChange={(e) => setDonationAmount(e.target.value)}
                        placeholder="0"
                        className="bg-[#F5F5F0]"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Mesaj (opțional)</Label>
                      <Input
                        value={donationMessage}
                        onChange={(e) => setDonationMessage(e.target.value)}
                        placeholder="Un mesaj pentru comunitate..."
                        className="bg-[#F5F5F0]"
                      />
                    </div>

                    <Button
                      onClick={handleDonate}
                      disabled={loading || !donationAmount}
                      className="w-full bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Donează'}
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border-none card-shadow">
                  <CardHeader>
                    <CardTitle className="font-serif text-xl text-[#1A4D2E]">
                      Istoricul Donațiilor
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {donationHistory.length > 0 ? (
                      <div className="space-y-3">
                        {donationHistory.map((donation, i) => (
                          <div key={i} className="flex justify-between items-center p-3 bg-[#F5F5F0] rounded-lg">
                            <div>
                              <p className="font-medium">{donation.amount} RON</p>
                              <p className="text-xs text-[#4a5568]">
                                {new Date(donation.date).toLocaleDateString('ro-RO')}
                              </p>
                            </div>
                            <Heart className="w-4 h-4 text-red-500" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-[#4a5568] py-8">
                        Nu ai donații încă
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
